import { BaseVisualizer } from './BaseVisualizer';

/**
 * Tunnel ring structure for 3D depth effect
 */
interface TunnelRing {
  z: number;          // depth (0 = far, 1 = close to camera)
  rotation: number;   // rotation angle in radians
  radius: number;     // base radius
  thickness: number;  // ring thickness
  color: { h: number; s: number; l: number }; // HSL color
  segments: number;   // number of segments (8-16)
  pulse: number;      // pulsing effect 0-1
}

/**
 * TunnelVisualizer - Hypnotic 3D tunnel flight through space
 * 
 * Features:
 * - 3D perspective tunnel view (like flying through a tube)
 * - Concentric rings/segments fly toward camera
 * - Tunnel walls pulse with music
 * - Speed controlled by energy
 * - Colors based on frequencies
 * - Hyperspace jump, warp speed, vortex vibes
 */
export class TunnelVisualizer extends BaseVisualizer {
  private rings: TunnelRing[] = [];
  private speed: number = 0.01;
  private rotationSpeed: number = 0.02;
  private maxRings: number = 30;
  private flashIntensity: number = 0;
  private visualizerState: 'idle' | 'intro' | 'running' = 'idle';
  private introStartTime: number = 0;
  private introDuration: number = 2.5; // Match PsychedelicVisualizer
  private twistIntensity: number = 0;
  private hasStartedOnce: boolean = false;
  
  constructor(canvas: HTMLCanvasElement) {
    super(canvas, 180);
  }

  /**
   * Main render loop
   */
  protected render(): void {
    const { x: centerX, y: centerY } = this.getCenter();
    
    // Handle different states
    if (this.visualizerState === 'idle') {
      this.renderIdleState(centerX, centerY);
      return;
    }
    
    if (this.visualizerState === 'intro') {
      this.renderIntroState(centerX, centerY);
      return;
    }
    
    // Running state - full tunnel effect
    this.renderRunningState(centerX, centerY);
  }

  /**
   * Idle state - Static tunnel with slow rotation
   */
  private renderIdleState(centerX: number, centerY: number): void {
    // Very dark background
    this.ctx.fillStyle = 'rgb(5, 5, 10)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Draw static rings
    if (this.rings.length === 0) {
      this.initializeIdleRings();
    }
    
    // Slowly rotate rings
    for (const ring of this.rings) {
      ring.rotation += 0.005;
    }
    
    this.drawRings(centerX, centerY);
    
    // Draw hint text
    this.ctx.save();
    this.ctx.fillStyle = this.hsl(180, 80, 60, 0.6);
    this.ctx.font = '48px monospace';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('🌀 TUNNEL', centerX, centerY);
    this.ctx.restore();
  }

  /**
   * Initialize static rings for idle state
   */
  private initializeIdleRings(): void {
    this.rings = [];
    const ringCount = 8;
    
    for (let i = 0; i < ringCount; i++) {
      const z = i / ringCount;
      const hue = 180 + (i * 30);
      
      this.rings.push({
        z: z,
        rotation: 0,
        radius: 150,
        thickness: 8,
        color: { h: hue, s: 80, l: 50 },
        segments: 12,
        pulse: 0
      });
    }
  }

  /**
   * Intro animation state
   */
  private renderIntroState(centerX: number, centerY: number): void {
    const elapsed = this.currentTime - this.introStartTime;
    const introProgress = Math.min(1, elapsed / this.introDuration);
    
    // Dark background with trail effect
    this.ctx.fillStyle = 'rgba(5, 5, 10, 0.2)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Phase 1 (0-25%): First ring appears at center
    if (introProgress < 0.25) {
      const phase = introProgress / 0.25;
      if (this.rings.length === 0) {
        this.spawnRing(0.5, 180);
      }
      this.rings[0].pulse = phase;
    }
    
    // Phase 2 (25-50%): More rings spawn, tunnel forms
    else if (introProgress < 0.5) {
      const phase = (introProgress - 0.25) / 0.25;
      const targetRings = Math.floor(phase * 15);
      
      while (this.rings.length < targetRings) {
        this.spawnRing();
      }
    }
    
    // Phase 3 (50-75%): Movement starts (slow speed)
    else if (introProgress < 0.75) {
      const phase = (introProgress - 0.5) / 0.25;
      this.speed = 0.002 * phase;
      
      // Keep spawning rings
      if (this.rings.length < 20) {
        if (Math.random() < 0.1) {
          this.spawnRing();
        }
      }
    }
    
    // Phase 4 (75-100%): Speed ramps up to full
    else {
      const phase = (introProgress - 0.75) / 0.25;
      const targetSpeed = 0.005 + this.currentEnergy * 0.02;
      this.speed = targetSpeed * phase;
      this.rotationSpeed = 0.02 * phase;
      
      // Flash at 98%
      if (introProgress > 0.98 && introProgress < 0.99) {
        this.flashIntensity = 1.0;
      }
    }
    
    // Update and draw rings
    this.updateRings();
    this.drawRings(centerX, centerY);
    this.drawFlash();
    
    // Transition to running state when complete
    if (introProgress >= 1.0) {
      this.visualizerState = 'running';
      console.log('🌀 Tunnel intro complete - entering hyperspace!');
    }
  }

  /**
   * Running state - Full tunnel effect
   */
  private renderRunningState(centerX: number, centerY: number): void {
    // Dark background with motion blur
    this.ctx.fillStyle = 'rgba(5, 5, 10, 0.15)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Optional: Draw stars in background
    this.drawStars(centerX, centerY);
    
    // Update speed based on energy
    const targetSpeed = 0.005 + this.currentEnergy * 0.02;
    this.speed = this.speed * 0.95 + targetSpeed * 0.05; // Smooth transition
    
    // Update rotation speed with twist effect
    this.rotationSpeed = 0.02 + this.twistIntensity * 0.1;
    this.twistIntensity *= 0.95; // Decay twist
    
    // Beat detection - match PsychedelicVisualizer (0.6 threshold, NO cooldown)
    if (this.currentBeat > 0.6) {
      this.onBeat();
    }
    
    // Bar detection - match PsychedelicVisualizer threshold
    if (this.currentBar > 0.75) {
      this.onBar();
    }
    
    // Update and draw rings
    this.updateRings();
    this.drawRings(centerX, centerY);
    this.drawFlash();
    
    // Spawn new rings as needed
    if (this.rings.length < this.maxRings) {
      if (Math.random() < 0.3) {
        this.spawnRing();
      }
    }
  }

  /**
   * Draw subtle stars in background
   */
  private drawStars(centerX: number, centerY: number): void {
    const starCount = 50;
    
    for (let i = 0; i < starCount; i++) {
      const angle = (i / starCount) * Math.PI * 2;
      const distance = 200 + (i % 10) * 50;
      const x = centerX + Math.cos(angle) * distance;
      const y = centerY + Math.sin(angle) * distance;
      const size = 1 + Math.random() * 1.5;
      const alpha = 0.3 + Math.random() * 0.4;
      
      this.ctx.fillStyle = this.hsl(200, 50, 80, alpha);
      this.ctx.beginPath();
      this.ctx.arc(x, y, size, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }

  /**
   * Update all rings - movement and cleanup
   */
  private updateRings(): void {
    // Update existing rings
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const ring = this.rings[i];
      
      // Move ring closer (increase Z)
      ring.z += this.speed;
      
      // Rotate ring
      ring.rotation += this.rotationSpeed;
      
      // Pulse decay
      ring.pulse *= 0.95;
      
      // Remove if too close (passed camera)
      if (ring.z > 1.0) {
        this.rings.splice(i, 1);
      }
    }
  }

  /**
   * Draw all rings with perspective
   */
  private drawRings(centerX: number, centerY: number): void {
    // Sort rings by Z (far to near) for proper depth
    const sortedRings = [...this.rings].sort((a, b) => a.z - b.z);
    
    // Draw from back to front
    for (const ring of sortedRings) {
      this.drawRing(ring, centerX, centerY);
    }
  }

  /**
   * Draw a single ring with perspective and segments
   */
  private drawRing(ring: TunnelRing, centerX: number, centerY: number): void {
    // Calculate perspective scale
    const scale = 1 / (ring.z + 0.1);
    const screenRadius = ring.radius * scale;
    
    // Skip if too small or too large
    if (screenRadius < 5 || screenRadius > this.canvas.width * 2) {
      return;
    }
    
    // Calculate segment dimensions
    const angleStep = (Math.PI * 2) / ring.segments;
    const segmentWidth = (screenRadius * angleStep) * 0.8; // 80% to create gaps
    const segmentHeight = ring.thickness * scale;
    
    // Calculate alpha based on depth and loudness
    const depthAlpha = 1 - (ring.z * 0.5); // Fade with distance
    const loudnessBoost = this.currentLoudness * 0.3;
    const alpha = Math.max(0.2, Math.min(1, depthAlpha + loudnessBoost));
    
    // Calculate brightness with pulse
    const baseLightness = ring.color.l + (ring.pulse * 30);
    const loudnessBrightness = baseLightness + (this.currentLoudness * 20);
    
    // Draw each segment
    for (let i = 0; i < ring.segments; i++) {
      const angle = i * angleStep + ring.rotation;
      
      // Calculate segment position
      const x = centerX + Math.cos(angle) * screenRadius;
      const y = centerY + Math.sin(angle) * screenRadius;
      
      // Draw segment as rectangle
      this.ctx.save();
      this.ctx.translate(x, y);
      this.ctx.rotate(angle);
      
      // Main segment
      this.ctx.fillStyle = this.hsl(
        ring.color.h,
        ring.color.s,
        loudnessBrightness,
        alpha
      );
      this.ctx.fillRect(-segmentWidth / 2, -segmentHeight / 2, segmentWidth, segmentHeight);
      
      // Glow effect
      if (this.currentLoudness > 0.5) {
        this.ctx.shadowBlur = 10 + this.currentLoudness * 20;
        this.ctx.shadowColor = this.hsl(ring.color.h, 100, 70, 0.5);
        this.ctx.fillRect(-segmentWidth / 2, -segmentHeight / 2, segmentWidth, segmentHeight);
      }
      
      this.ctx.restore();
    }
  }

  /**
   * Spawn a new ring at the far end of the tunnel
   */
  private spawnRing(zPosition: number = 0, fixedHue?: number): void {
    // Get dominant pitch for color
    let hue = fixedHue !== undefined ? fixedHue : 180;
    
    if (fixedHue === undefined && this.currentPitches.length > 0) {
      // Find dominant pitch
      let maxPitch = 0;
      let maxIndex = 0;
      
      for (let i = 0; i < this.currentPitches.length; i++) {
        if (this.currentPitches[i] > maxPitch) {
          maxPitch = this.currentPitches[i];
          maxIndex = i;
        }
      }
      
      // Map pitch to color
      // Low pitch (0-3) = Blue/Purple (200-280)
      // Mid pitch (4-7) = Cyan/Green (150-200)
      // High pitch (8-11) = Yellow/White (40-80)
      if (maxIndex < 4) {
        hue = 200 + maxIndex * 20;
      } else if (maxIndex < 8) {
        hue = 150 + (maxIndex - 4) * 12;
      } else {
        hue = 40 + (maxIndex - 8) * 10;
      }
    }
    
    // Create ring
    const ring: TunnelRing = {
      z: zPosition,
      rotation: Math.random() * Math.PI * 2,
      radius: 150 + Math.random() * 50,
      thickness: 8 + Math.random() * 4,
      color: { h: hue, s: 90, l: 50 },
      segments: 8 + Math.floor(Math.random() * 9), // 8-16 segments
      pulse: 0
    };
    
    this.rings.push(ring);
  }

  /**
   * Beat effect - rings pulse, spawn new ring, flash
   * Made MORE dramatic to match PsychedelicVisualizer
   */
  private onBeat(): void {
    // DRAMATIC pulse all rings
    for (const ring of this.rings) {
      ring.pulse = Math.min(1, ring.pulse + this.currentBeat);
    }
    
    // Spawn multiple rings for stronger beats
    const ringCount = this.currentBeat > 0.8 ? 3 : 1;
    for (let i = 0; i < ringCount; i++) {
      this.spawnRing();
    }
    
    // STRONGER screen flash based on beat intensity
    this.flashIntensity = Math.min(0.7, 0.3 + this.currentBeat * 0.4);
    
    // Speed boost based on beat intensity
    this.speed += this.currentBeat * 0.01;
    
    // Brief rotation speed increase
    this.rotationSpeed *= (1 + this.currentBeat * 0.8);
  }

  /**
   * Bar effect - twist tunnel, color shift, bigger flash
   * Made MORE dramatic to match PsychedelicVisualizer
   */
  private onBar(): void {
    // DRAMATIC twist effect - reverse rotation direction
    this.rotationSpeed *= -1.5;
    this.twistIntensity = 1.0;
    
    // Color shift all rings with bar intensity
    const hueShift = 60 + this.currentBar * 40;
    for (const ring of this.rings) {
      ring.color.h = (ring.color.h + hueShift) % 360;
    }
    
    // BIGGER flash based on bar intensity
    this.flashIntensity = Math.min(0.9, 0.6 + this.currentBar * 0.3);
    
    // DRAMATIC pulse all rings
    for (const ring of this.rings) {
      ring.pulse = 1.0;
    }
    
    // Speed burst
    this.speed += this.currentBar * 0.02;
  }

  /**
   * Draw flash overlay
   */
  private drawFlash(): void {
    if (this.flashIntensity > 0.01) {
      this.ctx.fillStyle = this.hsl(180, 80, 70, this.flashIntensity * 0.3);
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      
      this.flashIntensity *= 0.85; // Decay
    }
  }

  /**
   * Start intro animation
   * Don't restart if already running (just create flash/twist on track change)
   */
  public startIntro(): void {
    if (this.hasStartedOnce && this.visualizerState === 'running') {
      // Just create flash/twist, don't restart intro
      console.log('🌀 Tunnel flash/twist (already running)');
      this.flashIntensity = 0.4;
      this.rotationSpeed *= -1; // Twist direction change
      this.twistIntensity = 1.0;
      return;
    }
    
    console.log('🌀 Starting tunnel intro animation...');
    
    // Reset state
    this.rings = [];
    this.speed = 0;
    this.rotationSpeed = 0;
    this.flashIntensity = 0;
    this.twistIntensity = 0;
    
    // Start intro
    this.visualizerState = 'intro';
    this.introStartTime = this.currentTime;
    this.hasStartedOnce = true;
  }
}
